import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Sender, Bubble } from '@ant-design/x';
import { Typography, Button, Space } from 'antd';
import { Edit2, Check, X } from 'lucide-react';
import {
  parseAndSanitizeMarkdown,
  MarkdownStyles,
  mdParser
} from '../../../../utils/markdownUtils';
import { updateMessageContent } from '../../../../store/slices/gpt.slice';

// Separate Editor component for better organization
const MarkdownEditor = ({ initialContent, onSave, onCancel }) => {
  const [editedContent, setEditedContent] = useState(initialContent);

  return (
    <div className="relative w-full border rounded-lg p-4 bg-white shadow-sm">
      <textarea
        value={editedContent}
        onChange={(e) => setEditedContent(e.target.value)}
        className="w-full min-h-[200px] p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="flex justify-end gap-2 mt-2">
        <Button
          size="small"
          icon={<X className="w-4 h-4" />}
          onClick={onCancel}
          className="text-gray-600 hover:text-gray-800"
        >
          Cancel
        </Button>
        <Button
          type="primary"
          size="small"
          icon={<Check className="w-4 h-4" />}
          onClick={() => onSave(editedContent)}
          className="bg-blue-500"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const Blurb = ({
  role,
  content,
  shape = 'round',
  messageId,  // New prop for message identification
  conversationId // New prop for conversation identification
}) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const isUser = role === 'user';
  const placement = isUser ? 'end' : 'start';
  const variant = isUser ? 'filled' : 'outlined';


  const effectiveMessageId = messageId || `${conversationId}_assistant`;



  const bubbleStyle = {
    marginBottom: '10px',
    maxWidth: '70%',
    ...(isUser ? { marginLeft: 'auto' } : { marginRight: 'auto' })
  };

  const handleSave = (newContent) => {
    dispatch(updateMessageContent({
      conversationId,
      messageId: effectiveMessageId,
      content: newContent
    }));
    setIsEditing(false);
  };

  // Only show edit button for assistant messages
  const EditButton = !isUser && !isEditing && (
    <Button
      type="text"
      size="small"
      icon={<Edit2 className="w-4 h-4" />}
      onClick={() => setIsEditing(true)}
      className="opacity-0 group-hover:opacity-100 absolute top-2 right-2 text-gray-400 hover:text-gray-600"
    />
  );

  // Renders content as Markdown
  const messageRender = (text) => (
    <Typography>
      <div
        className="markdown-content"
        dangerouslySetInnerHTML={{
          __html: mdParser.render(text),
        }}
      />
    </Typography>
  );

  if (isEditing) {
    return (
      <div className={`${isUser ? 'ml-auto' : 'mr-auto'}`} style={bubbleStyle}>
        <MarkdownEditor
          initialContent={content}
          onSave={handleSave}
          onCancel={() => setIsEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="relative group">
      <Bubble
        placement={placement}
        content={content}
        messageRender={messageRender}
        variant={variant}
        shape={shape}
        style={bubbleStyle}
      />
      {EditButton}
    </div>
  );
};

export default Blurb;